import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import SarIcon from "../../icons/SarIcon";
import SignaturePadField from "../../common/SignaturePadField";
import { formatApplied, formatSar, statusLabel } from "./loanFormatters";

/** Label + value pair inside the Deal Summary card. */
const SummaryField = ({ icon, label, value, sub }) => (
  <Box>
    <Stack direction="row" spacing={0.5} alignItems="center">
      {icon}
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>
        {label}
      </Typography>
    </Stack>
    <Typography sx={{ fontSize: 14, color: "text.strong", mt: 0.5 }}>
      {value}
    </Typography>
    {sub ? (
      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
        {sub}
      </Typography>
    ) : null}
  </Box>
);

/**
 * Final administrative sign-off. The signature is drawn inline and posted with
 * the same payload shape the investor confirm-sign call uses.
 */
const AdminLoanSignOffModal = ({
  open,
  loan,
  onClose,
  onSubmit,
  submitting,
  error,
}) => {
  const [notes, setNotes] = React.useState("");
  const [signature, setSignature] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setNotes("");
    setSignature(null);
  }, [open, loan?._id]);

  const iconSx = { fontSize: 12, color: "text.secondary" };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
        },
      }}
    >
      <Box sx={{ p: 3, position: "relative" }}>
        <IconButton
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
          sx={{ position: "absolute", top: 12, right: 12, color: "text.muted", opacity: 0.7 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>

        {/* Title */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 5 }}>
          <DescriptionOutlinedIcon
            sx={{ fontSize: 20, color: (theme) => theme.palette.accent.blue.solid }}
          />
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.strong" }}>
            Final Sign-Off &amp; Trade Confirmation
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 1 }}>
          Review the deal details and provide your administrative sign-off to complete the
          transaction.
        </Typography>

        {/* Deal summary */}
        <Box
          sx={{
            mt: 2,
            p: 2.125,
            borderRadius: 1.5,
            bgcolor: "background.subtle",
            border: (theme) => `1px solid ${theme.palette.border.light}`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <AssessmentOutlinedIcon
              sx={{ fontSize: 16, color: (theme) => theme.palette.accent.blue.solid }}
            />
            <Typography
              sx={{ fontSize: 16, color: (theme) => theme.palette.accent.blue.solid }}
            >
              Deal Summary
            </Typography>
          </Stack>

          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              rowGap: 2,
              columnGap: 2,
            }}
          >
            <SummaryField label="Asset" value={loan?.assetName || "—"} />
            <SummaryField label="Loan ID" value={loan?.loanId || "—"} />
            <SummaryField
              icon={<PersonOutlineIcon sx={iconSx} />}
              label="Investor"
              value={loan?.user?.name || "—"}
              sub={loan?.user?.email}
            />
            <SummaryField
              icon={<SarIcon sx={iconSx} />}
              label="Amount"
              value={formatSar(loan?.amount, loan?.currency)}
            />
            <SummaryField
              icon={<CalendarTodayIcon sx={iconSx} />}
              label="Date"
              value={formatApplied(loan?.appliedAt)}
            />
            <SummaryField
              label="Status"
              value={statusLabel(loan?.status)}
            />
          </Box>
        </Box>

        {/* Administrative notes */}
        <Typography
          sx={{ fontSize: 14, fontWeight: 600, color: "text.strong", mt: 3, mb: 1 }}
        >
          Administrative Notes (Optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={submitting}
          placeholder="Add any final notes or comments regarding this transaction..."
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.25, fontSize: 14 } }}
        />

        {/* Administrative signature */}
        <Typography
          sx={{ fontSize: 14, fontWeight: 600, color: "text.strong", mt: 3, mb: 1 }}
        >
          Administrative Signature
        </Typography>
        <SignaturePadField
          height={200}
          emptyHint="Draw your signature in the box above to sign off"
          disabled={submitting}
          onChange={setSignature}
        />

        {/* Compliance note */}
        <Box
          sx={{
            mt: 3,
            px: 2.125,
            py: 1.625,
            borderRadius: 1.5,
            bgcolor: "background.paper",
            border: (theme) => `1px solid ${theme.palette.border.light}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <InfoOutlinedIcon
              sx={{ fontSize: 16, mt: 0.25, flexShrink: 0, color: "text.secondary" }}
            />
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              By signing off on this transaction, you confirm that all compliance checks
              have been completed and the deal can proceed. A trade confirmation will be
              generated and sent to all parties.
            </Typography>
          </Stack>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}

        {/* Actions */}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{
              height: 36,
              px: 2.125,
              borderRadius: 1.25,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              borderColor: "border.light",
              color: "text.strong",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => signature && onSubmit?.({ signatureData: signature, notes })}
            disabled={!signature || submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={14} sx={{ color: "common.white" }} />
              ) : (
                <TaskAltIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              height: 36,
              px: 1.5,
              borderRadius: 1.25,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              color: "common.white",
              bgcolor: (theme) => theme.palette.accent.blue.solid,
              boxShadow: "none",
              "&:hover": {
                bgcolor: (theme) => theme.palette.accent.blue.actionHover,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                opacity: 0.5,
                color: (theme) => theme.palette.common.white,
                bgcolor: (theme) => theme.palette.accent.blue.solid,
              },
            }}
          >
            {submitting ? "Signing Off…" : "Complete Sign-Off"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default AdminLoanSignOffModal;
